// PoolScreener core — pure math primitives. Each is a total function with
// explicit types; no indexing that could return undefined leaks out (the
// tsconfig has noUncheckedIndexedAccess). Formula sources are cited inline
// and pinned by the unit fixtures in screens.test.ts.
import { TRADING_DAYS_PER_YEAR } from './types';

/** Arithmetic mean. Returns 0 for an empty list (callers guard length). */
export function mean(xs: number[]): number {
  if (xs.length === 0) return 0;
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

/** Sample standard deviation (n−1). Returns 0 for fewer than 2 points. */
export function stdev(xs: number[]): number {
  const n = xs.length;
  if (n < 2) return 0;
  const m = mean(xs);
  const ss = xs.reduce((a, b) => a + (b - m) * (b - m), 0);
  return Math.sqrt(ss / (n - 1));
}

/** Log returns ln(cₜ / cₜ₋₁) from a close series. Skips non-positive or
 *  missing points so one bad candle can't NaN the whole series. */
export function logReturns(closes: number[]): number[] {
  const out: number[] = [];
  for (let i = 1; i < closes.length; i++) {
    const prev = closes[i - 1];
    const cur = closes[i];
    if (prev === undefined || cur === undefined || prev <= 0 || cur <= 0) continue;
    out.push(Math.log(cur / prev));
  }
  return out;
}

/** Realized daily volatility = stdev of daily log returns. */
export function realizedVolDaily(closes: number[]): number {
  return stdev(logReturns(closes));
}

/** Annualize a daily vol by √365 (GBM scaling). */
export function annualizeVol(sigmaDaily: number): number {
  return sigmaDaily * Math.sqrt(TRADING_DAYS_PER_YEAR);
}

/**
 * LVR (loss-versus-rebalancing) bleed rate per day, as a fraction of pool
 * value, for a constant-product pool: σ²/8.
 * Milionis–Moallemi–Roughgarden–Zhang, arXiv:2208.06046.
 */
export function lvrDaily(sigmaDaily: number): number {
  return (sigmaDaily * sigmaDaily) / 8;
}

/** Annualized LVR = daily LVR × 365. */
export function lvrAnnual(sigmaDaily: number): number {
  return lvrDaily(sigmaDaily) * TRADING_DAYS_PER_YEAR;
}

/**
 * Pool break-even implied daily volatility: the σ at which the daily fee
 * income (γ·V/L) exactly offsets the σ²/8 LVR bleed. Setting γV/L = σ²/8 and
 * solving gives σ = √(8·γ·V/L) = 2·√(2·γ·feeTier·… ) — i.e.
 *
 *     IV_daily = 2 · √(2 · feeTier · dailyVolume / L)
 *
 * where L is the pool's USD liquidity base (totalValueLockedUSD), the SAME
 * base the fee return is measured against so this stays self-consistent with
 * lvrDaily. NOTE the feeTier lives INSIDE the root — an earlier form put it
 * outside (2·γ·√(V/L)), understating IV ~26× and failing every pool.
 * Returns 0 when liquidity is non-positive (caller treats as insufficient data).
 */
export function lambertIvDaily(
  feeTier: number,
  dailyVolume: number,
  tickLiquidityUsd: number,
): number {
  if (tickLiquidityUsd <= 0 || dailyVolume < 0 || feeTier < 0) return 0;
  return 2 * Math.sqrt((2 * feeTier * dailyVolume) / tickLiquidityUsd);
}

/** Coefficient of variation = stdev / mean. Returns 0 when mean is ~0. */
export function coefficientOfVariation(xs: number[]): number {
  const m = mean(xs);
  if (Math.abs(m) < 1e-12) return 0;
  return stdev(xs) / m;
}

/**
 * Liquidation price of a short perp at a given leverage, isolated margin,
 * ignoring maintenance margin (a conservative-for-headroom simplification):
 * the short is liquidated when the loss equals the posted margin, i.e. price
 * rises by 1/leverage. liq = entry · (1 + 1/leverage).
 */
export function shortLiquidationPrice(entryPrice: number, leverage: number): number {
  if (leverage <= 0) return Infinity;
  return entryPrice * (1 + 1 / leverage);
}

/**
 * The largest leverage whose short liquidation price still sits at
 * `targetPrice` (used to report "max safe leverage" when Screen 5 fails).
 * Inverts shortLiquidationPrice: from entry·(1+1/L)=target → L=1/(target/entry−1).
 */
export function maxSafeLeverageForShort(entryPrice: number, targetPrice: number): number {
  const rise = targetPrice / entryPrice - 1;
  if (rise <= 0) return Infinity;
  return 1 / rise;
}

/**
 * Expected number of days a ±rangeWidth concentrated position stays in range
 * before the price exits, modelling log-price as a driftless random walk with
 * daily vol σ. First-passage time of the band [ln(1−w), ln(1+w)] from centre:
 *   E[τ] = |ln(1−w)| · ln(1+w) / σ²   (days).
 * Driftless is a deliberate simplification — real trends make one-sided exits
 * faster, so this is an estimate. Returns Infinity when σ≤0 (never moves) or the
 * width is out of (0,1).
 */
export function expectedDaysInRange(sigmaDaily: number, rangeWidth: number): number {
  if (sigmaDaily <= 0 || rangeWidth <= 0 || rangeWidth >= 1) return Infinity;
  const lower = Math.abs(Math.log(1 - rangeWidth));
  const upper = Math.log(1 + rangeWidth);
  return (lower * upper) / (sigmaDaily * sigmaDaily);
}

/** Expected range exits (≈ redeploys) per year for a ±rangeWidth position at
 *  daily vol σ. 0 when σ≤0 (the position never exits). */
export function exitsPerYear(sigmaDaily: number, rangeWidth: number): number {
  const days = expectedDaysInRange(sigmaDaily, rangeWidth);
  return Number.isFinite(days) && days > 0 ? TRADING_DAYS_PER_YEAR / days : 0;
}
